export interface BuildVoiceLiveWsUrlParams {
  /** Backend HTTP(S) origin, e.g. `https://host:8000` (the avatar/voice backend). */
  backendUrl: string;
  /** Authenticated user id. */
  userId: string;
  /** Scenario id for the roleplay. */
  scenarioId: string;
  /** Existing FieldOne session id; appended only when reconnecting. */
  sessionId?: number | null;
  /** Whether this is a reconnect (controls whether `session_id` is sent). */
  isReconnect?: boolean;
  /** Whether to request an audio-only session (no avatar/WebRTC). */
  audioMode?: boolean;
  /** Stable canonical usage run UUID for the mounted conversation. */
  usageRunId: string;
  /** Stable UTC start timestamp for the canonical usage run. */
  usageRunStartedAt: string;
}

export interface VoiceLiveWsUrl {
  /** Full `ws://`/`wss://` URL to open. */
  wsUrl: string;
  /** Backend host (including port) parsed from `backendUrl`, for telemetry. */
  host: string;
}

/**
 * Build the Voice Live WebSocket URL from the backend origin and session
 * parameters. `https:` origins map to `wss:`, anything else to `ws:`.
 * `session_id` is included only on reconnect (so the backend reuses the
 * existing FieldOne session); `mode=audio` is added for audio-only sessions.
 */
export const buildVoiceLiveWsUrl = ({
  backendUrl,
  userId,
  scenarioId,
  sessionId,
  isReconnect = false,
  audioMode = false,
  usageRunId,
  usageRunStartedAt,
}: BuildVoiceLiveWsUrlParams): VoiceLiveWsUrl => {
  const url = new URL(backendUrl);
  const wsProtocol = url.protocol === 'https:' ? 'wss:' : 'ws:';
  const params = new URLSearchParams({
    user_id: userId,
    scenario_id: scenarioId,
    usage_run_id: usageRunId,
    usage_run_started_at: usageRunStartedAt,
  });
  if (isReconnect && sessionId) params.set('session_id', String(sessionId));
  if (audioMode) params.set('mode', 'audio');
  const wsUrl = `${wsProtocol}//${url.host}/speech/api/ws/voice-live?${params.toString()}`;
  return { wsUrl, host: url.host };
};
